import React, { useRef, useState } from "react";
import { ChevronDown, Sparkles, Copy, Pencil, Trash2, MoreHorizontal, Eye } from "lucide-react";
import { toast } from "sonner";
import ToneChip from "@/components/ToneChip";
import SpeakerButton from "@/components/SpeakerButton";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useSpeechPlayback } from "@/lib/useSpeechPlayback";
import { track, bumpLocalCounter } from "@/lib/analytics";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const STYLE_LABELS = {
    concise: "Concise",
    warm: "Warm",
    firm: "Firm",
    professional: "Professional",
    gentle: "Gentle",
};

const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });

const LONG_PRESS_MS = 500;

const ChatBubble = ({
    message,
    onCopy,
    onEdit,
    onDelete,
    onRecheck,
    expandedOriginal = false,
    onToggleOriginal,
    showRewriteComparison = true,
    showEmotionalNudges = true,
}) => {
    const mine = message.sender === "me";
    const [showCoaching, setShowCoaching] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const hasCoaching =
        Array.isArray(message.coaching) &&
        message.coaching.length > 0 &&
        showEmotionalNudges;
    const isImproved = message.variant === "improved";
    const styleLabel =
        STYLE_LABELS[message.style] ||
        (message.style
            ? message.style.charAt(0).toUpperCase() + message.style.slice(1)
            : null);

    // Audio playback for AI-rewritten messages only.
    const playback = useSpeechPlayback();
    const showSpeaker = isImproved && playback.isSupported;
    const isPlaying = showSpeaker && playback.speakingId === message.id;
    const togglePlayback = () => playback.toggle(message.id, message.text || "");

    // "View Original" — show on EVERY rewritten bubble, unconditionally.
    // We deliberately do not gate this on `showRewriteComparison` anymore.
    // Hiding it caused real-world confusion: users repeatedly reported the
    // option as "missing" because the toggle's effect (silently removing
    // a contextual action) is invisible. The preference still affects
    // future inline comparison features, but the basic affordance for
    // seeing the source of a rewrite is now always available.
    const canShowOriginal = isImproved;
    const hasOriginalText = !!message.originalText;
    const doToggleOriginal = () => {
        if (!canShowOriginal) return;
        if (typeof onToggleOriginal === "function") {
            onToggleOriginal(message.id);
        }
    };

    // ---------- Actions ----------
    const doCopy = async () => {
        const ok = await copyTextToClipboard(message.text || "");
        if (ok) {
            toast.success("Copied");
            track("message_copied", { variant: message.variant || "plain" });
            bumpLocalCounter("message_copied");
            if (typeof onCopy === "function") onCopy(message.text || "");
        } else {
            toast.error("Couldn't copy — please select and copy manually.");
        }
    };

    const doEdit = () => {
        if (typeof onEdit === "function") onEdit(message.text || "");
    };

    const doDelete = () => {
        if (typeof onDelete === "function") onDelete(message);
    };

    const doRecheck = () => {
        if (typeof onRecheck === "function") onRecheck(message.text || "");
    };

    // ---------- Mobile long-press → open menu ----------
    const longPressTimer = useRef(null);
    const longPressFired = useRef(false);

    const startLongPress = () => {
        longPressFired.current = false;
        longPressTimer.current = window.setTimeout(() => {
            longPressFired.current = true;
            setMenuOpen(true);
        }, LONG_PRESS_MS);
    };
    const cancelLongPress = () => {
        if (longPressTimer.current) {
            window.clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };
    const onContextMenu = (e) => {
        // Long-press on iOS triggers contextmenu — also catch right-click on desktop.
        e.preventDefault();
        setMenuOpen(true);
    };

    // ---------- Keyboard shortcut: Delete to remove a focused bubble (desktop) ----------
    const onBubbleKeyDown = (e) => {
        if (e.key === "Delete" || e.key === "Backspace") {
            // Only treat plain Delete (no modifier) as a quick action.
            if (!e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                doDelete();
            }
        } else if (e.key.toLowerCase() === "e" && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            doEdit();
        } else if (e.key.toLowerCase() === "c" && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            doCopy();
        }
    };

    return (
        <div
            data-testid="chat-bubble"
            data-variant={message.variant || "plain"}
            data-style={message.style || undefined}
            className={`tg-enter flex ${mine ? "justify-end" : "justify-start"} w-full`}
        >
            <div
                className={`max-w-[82%] sm:max-w-[70%] flex flex-col gap-1.5 ${
                    mine ? "items-end" : "items-start"
                }`}
            >
                <div className="group relative">
                    <div
                        tabIndex={0}
                        role="article"
                        aria-label="Message"
                        onTouchStart={startLongPress}
                        onTouchEnd={cancelLongPress}
                        onTouchMove={cancelLongPress}
                        onContextMenu={onContextMenu}
                        onKeyDown={onBubbleKeyDown}
                        className={`relative px-4 py-3 text-[15px] leading-relaxed shadow-sm whitespace-pre-wrap break-words outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                            showSpeaker ? "pr-10" : ""
                        } ${
                            mine
                                ? "bg-stone-800 text-stone-100 rounded-2xl rounded-tr-sm"
                                : "bg-white border border-stone-200 text-stone-800 rounded-2xl rounded-tl-sm"
                        }`}
                    >
                        {message.text}
                        {showSpeaker && (
                            <span className="absolute top-5 right-2 z-10">
                                <SpeakerButton
                                    isPlaying={isPlaying}
                                    onToggle={togglePlayback}
                                    variant={mine ? "dark" : "light"}
                                />
                            </span>
                        )}
                    </div>

                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="btn-bubble-menu"
                                type="button"
                                aria-label="Message actions"
                                className="absolute -top-2 -right-2 h-7 w-7 rounded-full flex items-center justify-center bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 shadow-sm transition-all opacity-60 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 data-[state=open]:text-foreground data-[state=open]:border-foreground/40 sm:opacity-50 sm:group-hover:opacity-100"
                            >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            sideOffset={10}
                            collisionPadding={12}
                            data-testid="bubble-action-menu"
                            className="min-w-[180px] z-[60] bg-white border-stone-200 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)]"
                            onCloseAutoFocus={(e) => {
                                // Don't yank focus back to the ⋯ trigger — let our
                                // action handlers (Edit/Copy) place focus where it
                                // makes sense (the composer).
                                e.preventDefault();
                            }}
                        >
                            {typeof onRecheck === "function" && (
                                <DropdownMenuItem
                                    data-testid="menu-action-recheck"
                                    onClick={doRecheck}
                                    className="gap-2 text-[#4B6143] focus:text-[#2f3d29] focus:bg-[#EAF1E8]/60"
                                >
                                    <Sparkles className="h-3.5 w-3.5" />
                                    Recheck tone
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                data-testid="menu-action-edit"
                                onClick={doEdit}
                                className="gap-2"
                            >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="menu-action-copy"
                                onClick={doCopy}
                                className="gap-2"
                            >
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                            </DropdownMenuItem>
                            {canShowOriginal && !expandedOriginal && (
                                <DropdownMenuItem
                                    data-testid="menu-action-view-original"
                                    data-expanded={expandedOriginal}
                                    onClick={doToggleOriginal}
                                    className="gap-2"
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    View Original
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                data-testid="menu-action-delete"
                                onClick={doDelete}
                                className="gap-2 text-[#A35741] focus:text-[#A35741] focus:bg-[#F7EBE8]/60"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Inline "View Original" panel. Subtle, lower visual priority
                    than the rewritten bubble. Lives BELOW the bubble and ABOVE
                    the meta row so the original feels contextually attached.
                    When the original text isn't stored on this bubble (legacy
                    rewrites from earlier app versions), we still render the
                    panel with a soft "not stored" hint so the menu action
                    never feels broken. */}
                {canShowOriginal && expandedOriginal && (
                    <div
                        data-testid="original-panel"
                        data-has-original={hasOriginalText}
                        className={`tg-enter w-full ${mine ? "self-end" : "self-start"} max-w-full`}
                    >
                        <div className="relative rounded-xl bg-stone-50 border border-stone-200/80 px-3 py-2 pr-12 text-stone-600">
                            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-stone-400 mb-0.5">
                                Original
                            </div>
                            {hasOriginalText ? (
                                <div className="text-[13px] leading-relaxed whitespace-pre-wrap break-words">
                                    {message.originalText}
                                </div>
                            ) : (
                                <div className="text-[12.5px] leading-relaxed italic text-stone-500">
                                    Original text wasn&apos;t saved for this
                                    rewrite. New rewrites will keep their
                                    originals automatically.
                                </div>
                            )}
                            <button
                                data-testid="btn-hide-original"
                                type="button"
                                onClick={doToggleOriginal}
                                aria-label="Hide original"
                                className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-[0.1em] text-stone-500 hover:text-stone-800 hover:bg-stone-200/70 transition-colors"
                            >
                                Hide
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 px-1 flex-wrap">
                    {isImproved && (
                        <span className="text-[10px] uppercase tracking-[0.12em] text-[#5E7153] font-semibold">
                            rewritten
                        </span>
                    )}
                    {isImproved && styleLabel && (
                        <span
                            data-testid="rewrite-style-chip"
                            data-style={message.style}
                            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-[#D5E3D1] bg-[#EAF1E8]/60 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#4B6143]"
                        >
                            <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                            {styleLabel}
                        </span>
                    )}
                    {!isImproved && (message.tone || message.signal) ? (
                        <ToneChip
                            signal={message.signal}
                            risk={message.risk}
                            tone={message.tone}
                        />
                    ) : null}
                    {hasCoaching && (
                        <button
                            data-testid="btn-why-this-tone"
                            type="button"
                            onClick={() => setShowCoaching((s) => !s)}
                            aria-expanded={showCoaching}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Sparkles className="h-3 w-3" />
                            {showCoaching ? "Hide coaching" : "Why this tone?"}
                            <ChevronDown
                                className={`h-3 w-3 transition-transform ${
                                    showCoaching ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                        {formatTime(message.createdAt)}
                    </span>
                </div>
                {hasCoaching && showCoaching && (
                    <div
                        data-testid="coaching-panel"
                        className={`tg-enter mt-1 rounded-xl border border-border bg-card px-4 py-3 text-[13px] leading-relaxed text-foreground/80 max-w-full ${
                            mine ? "text-right" : "text-left"
                        }`}
                    >
                        <ul
                            className={`space-y-1.5 ${mine ? "text-left" : ""}`}
                        >
                            {message.coaching.slice(0, 3).map((b, i) => (
                                <li
                                    key={i}
                                    data-testid={`coaching-bullet-${i}`}
                                    className="flex gap-2"
                                >
                                    <span
                                        aria-hidden
                                        className="mt-1.5 h-1 w-1 rounded-full bg-[#5E7153] shrink-0"
                                    />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatBubble;
